/**
 * Local-dev fallback for values normally provided at runtime by
 * `window.globalConfig`. Vite bakes VITE_-prefixed vars into `import.meta.env`,
 * which is a real object at runtime, so dynamic key access works here.
 */
const readViteEnv = (varName: string): string | undefined => {
  const value = import.meta.env[`VITE_${varName}`];

  return typeof value === "string" ? value : undefined;
};

export const getVariableFromEnv = (
  varName: string,
  defaultValue: string = "",
): string => {
  // @ts-expect-error - `window.globalConfig` is injected at runtime by
  // public/appConfig.js and is not declared on the Window type.
  const globalConfig = window.globalConfig;

  if (globalConfig) {
    return globalConfig[varName] || defaultValue;
  }

  return readViteEnv(varName) || defaultValue;
};

export const getBooleanFromEnv = (
  varName: string,
  defaultValue = false,
): boolean => {
  // @ts-expect-error - `window.globalConfig` is injected at runtime by
  // public/appConfig.js and is not declared on the Window type.
  const raw = window.globalConfig?.[varName];
  if (typeof raw === "boolean") {
    return raw;
  }
  if (typeof raw === "string") {
    return raw.toLowerCase() === "true";
  }
  const fallback = readViteEnv(varName);
  if (typeof fallback === "string") {
    return fallback.toLowerCase() === "true";
  }
  return defaultValue;
};

/**
 * The Quality Lab is still being built, so its menu entry is opt-in per
 * environment: set `ENABLE_QUALITY_LAB: true` in that environment's appConfig.js.
 *
 * Absent, it falls back to whether this is a development build. That is the safe
 * default in both directions: an environment nobody has configured yet does not
 * show unfinished work, and a developer whose appConfig.js predates this flag
 * does not lose the screen they are working on.
 *
 * Only the menu entry is gated. The routes still answer, so a link already handed
 * out keeps working and anyone testing can reach it by URL.
 */
export const QUALITY_LAB_ENABLED = getBooleanFromEnv(
  "ENABLE_QUALITY_LAB",
  getVariableFromEnv("ENVIRONMENT", "development").toLowerCase() ===
    "development",
);

export const METRICS_ROLE_ID = getVariableFromEnv("AUTH0_METRICS_ROLE_ID");
export const ADMIN_ROLE_ID = getVariableFromEnv("AUTH0_ADMIN_ROLE_ID");
export const STP_ROLE_ID = getVariableFromEnv("AUTH0_STP_ROLE_ID");

const getStringArrayFromEnv = (varName: string): string[] => {
  // @ts-expect-error - `window.globalConfig` is injected at runtime by
  // public/appConfig.js and is not declared on the Window type.
  const raw = window.globalConfig?.[varName];
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.length > 0) {
    return raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  const fallback = readViteEnv(varName);
  if (typeof fallback === "string" && fallback.length > 0) {
    return fallback
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

export const INTERNAL_TOOLS_USER_IDS = getStringArrayFromEnv(
  "INTERNAL_TOOLS_USER_IDS",
);

export const isInternalToolsUser = (user?: { id: string } | null): boolean =>
  !!user?.id && INTERNAL_TOOLS_USER_IDS.includes(user.id);

/**
 * Service URL overrides for local development.
 * Only active when ENVIRONMENT is "development".
 *
 * To override locally, add to appConfig.js:
 *   NORMALIZATION_API_URL: "http://localhost:8081",
 *   PUBLIC_API_DATA_URL: "http://localhost:8082",
 *   API_CRUD_URL: "http://localhost:8080",
 */
const SERVICE_URLS = {
  NORMALIZATION_API: "API_BASE_URL",
  PUBLIC_API_DATA: "API_BASE_URL",
  API_CRUD: "API_BASE_URL",
  CUSTOMER_NORMALIZATION_API: "API_BASE_URL",
} as const;

type ServiceName = keyof typeof SERVICE_URLS;

export const getServiceUrl = (service: ServiceName): string => {
  // Allows overriding individual service URLs (e.g. NORMALIZATION_API_URL, PUBLIC_API_DATA_URL) for local development.
  if (getVariableFromEnv("ENVIRONMENT") === "development") {
    const override = getVariableFromEnv(`${service}_URL`);
    if (override) {
      return override;
    }
  }

  return getVariableFromEnv(SERVICE_URLS[service]);
};
